/**
 * Deal Subscription System Type Definitions
 *
 * Types for the monthly subscription deal model that replaces
 * the legacy pay-per-use token system.
 */

/**
 * Deal status for an organization's subscription
 * Returned by get_current_org_deal_status and admin_get_org_deal_status RPCs
 */
export interface DealStatus {
  planKey: string;
  planDisplayName: string;
  dealsPerMonth: number;
  /**
   * Admin-granted one-time bonus for the current billing period. Resets to 0
   * on invoice.paid (cycle turnover). Added to dealsPerMonth to compute the
   * effective consumption ceiling. See Slice H migration 20260415120000.
   */
  bonusDealsThisPeriod: number;
  dealsUsedThisPeriod: number;
  /**
   * Remaining deals available this period. Computed against the effective
   * ceiling: (dealsPerMonth + bonusDealsThisPeriod) - dealsUsedThisPeriod.
   */
  dealsRemaining: number;
  subscriptionStatus: SubscriptionStatusType;
  hasStripeCustomer: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  daysUntilRenewal: number | null;
  cancelAtPeriodEnd: boolean;
}

export type SubscriptionStatusType =
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'trialing'
  | 'inactive';

/**
 * Raw response from Supabase RPC (snake_case)
 */
export interface DealStatusResponse {
  plan_key: string;
  plan_display_name?: string | null;
  display_name?: string | null;
  deals_per_month: number;
  /** Slice H (migration 20260415120000): admin-granted per-period bonus. */
  bonus_deals_this_period: number;
  deals_used_this_period: number;
  deals_remaining: number;
  subscription_status: string;
  has_stripe_customer?: boolean | null;
  current_period_start: string | null;
  current_period_end: string | null;
  days_until_renewal: number | null;
  cancel_at_period_end: boolean;
}

/**
 * Subscription plan definition from the subscription_plans table
 */
export interface SubscriptionPlan {
  id: string;
  planKey: string;
  displayName: string;
  priceMonthly: number;
  dealsPerMonth: number;
  features: string[];
  sortOrder: number;
  isActive: boolean;
}

/**
 * Raw subscription plan row from DB (snake_case)
 */
export interface SubscriptionPlanRow {
  id: string;
  plan_key: string;
  display_name: string;
  price_monthly: number;
  deals_per_month: number;
  features: string[];
  sort_order: number;
  is_active: boolean;
}

/**
 * Deal consumption log entry
 */
export interface DealConsumptionLog {
  id: string;
  organization_id: string;
  user_id: string;
  transaction_id: number | null;
  deals_consumed: number;
  deals_used_before: number;
  deals_used_after: number;
  deals_remaining: number;
  billing_period_start: string;
  billing_period_end: string;
  consumed_at: string;
  metadata: Record<string, unknown>;
}

/**
 * Subscription history event
 */
export interface SubscriptionHistoryEvent {
  id: string;
  organization_id: string;
  user_id: string;
  event_type: string;
  from_plan_key: string | null;
  to_plan_key: string | null;
  stripe_subscription_id: string | null;
  stripe_invoice_id: string | null;
  amount_charged: number | null;
  tax_amount: number | null;
  currency: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

/**
 * Custom error for insufficient deals
 */
export class InsufficientDealsError extends Error {
  constructor(
    public readonly required: number,
    public readonly available: number
  ) {
    super(`Insufficient deals: need ${required}, have ${available}`);
    this.name = 'InsufficientDealsError';
  }
}

/**
 * Custom error for no active subscription
 */
export class NoActiveSubscriptionError extends Error {
  constructor() {
    super('No active subscription found. Please subscribe to a plan to continue.');
    this.name = 'NoActiveSubscriptionError';
  }
}

/**
 * Custom error for expired billing period
 */
export class SubscriptionExpiredError extends Error {
  constructor(public readonly expiredDate?: string) {
    super(`Billing period has expired${expiredDate ? ` on ${expiredDate}` : ''}. Please renew your subscription.`);
    this.name = 'SubscriptionExpiredError';
  }
}

/**
 * Map snake_case RPC response to camelCase DealStatus
 */
export function mapDealStatusResponse(data: DealStatusResponse | null): DealStatus {
  if (!data) {
    return {
      planKey: '',
      planDisplayName: '',
      dealsPerMonth: 0,
      bonusDealsThisPeriod: 0,
      dealsUsedThisPeriod: 0,
      dealsRemaining: 0,
      subscriptionStatus: 'inactive',
      hasStripeCustomer: false,
      currentPeriodStart: null,
      currentPeriodEnd: null,
      daysUntilRenewal: null,
      cancelAtPeriodEnd: false,
    };
  }

  return {
    planKey: data.plan_key,
    planDisplayName: data.plan_display_name ?? data.display_name ?? '',
    dealsPerMonth: data.deals_per_month,
    // Tolerate pre-Slice-H RPC responses that don't carry the column:
    // they'll arrive as undefined, we default to 0 so downstream math
    // still produces a sensible effective ceiling.
    bonusDealsThisPeriod: data.bonus_deals_this_period ?? 0,
    dealsUsedThisPeriod: data.deals_used_this_period,
    dealsRemaining: data.deals_remaining,
    subscriptionStatus: data.subscription_status as SubscriptionStatusType,
    hasStripeCustomer: data.has_stripe_customer ?? false,
    currentPeriodStart: data.current_period_start,
    currentPeriodEnd: data.current_period_end,
    daysUntilRenewal: data.days_until_renewal,
    cancelAtPeriodEnd: data.cancel_at_period_end,
  };
}

/**
 * Map snake_case subscription plan row to camelCase
 */
export function mapSubscriptionPlanRow(row: SubscriptionPlanRow): SubscriptionPlan {
  return {
    id: row.id,
    planKey: row.plan_key,
    displayName: row.display_name,
    priceMonthly: row.price_monthly,
    dealsPerMonth: row.deals_per_month,
    features: row.features ?? [],
    sortOrder: row.sort_order,
    isActive: row.is_active,
  };
}
